import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getConversationID } from '@rsos/reduxDataStore/src/features/conversations/utils';
import {
  closeCard,
  LIVE_VIDEO_CARD,
  setCardsToRender,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { useCreateRoomMutation } from '@rsos/reduxDataStore/src/features/videoTo911/videoTo911Api';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import usePreconfiguredMessages from '../../TF911ChatBox/usePreconfiguredMessages';
import {
  trackStartVideoTo911ButtonClick,
  trackStartVT911Response,
} from '../../videoTo911Trackings';

const LIVESTREAM_PROMPT = 'Tap link to live-stream video to first responder.';

export function useVideoSessionInitialization({
  isForOverview,
  selectedVideoRoom,
  onStartConversation,
}) {
  const dispatch = useDispatch();
  const isVideoCardOpen = useSelector(
    state => !!state.mapCard?.cards?.[LIVE_VIDEO_CARD],
  );
  const preconfiguredMessages = usePreconfiguredMessages();
  const [triggerCreateRoom] = useCreateRoomMutation();
  const accountId = useSelector(state => state.psaps?.currentPSAP?.account_id);
  const selectedCallerId = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const selectedCall = useSelectedCall();
  const mostRecentLoc = getMostRecentLoc(selectedCall);
  const conversation = useSelector(
    state => state.conversations?.[selectedCallerId],
  );

  const tf911Conversation =
    useSelectedTextFrom911Conversation(selectedCallerId);

  const callStartTime = useMemo(() => {
    // If the callerID doesn't have location, use the call_start_time from the
    // outbound SMS
    return (
      mostRecentLoc?.call_start_time || tf911Conversation?.data?.call_start_time
    );
  }, [mostRecentLoc, tf911Conversation]);

  /**
   * Initiates the video session. Starts the TF911 chat if it doesn't already
   * exist, then creates the video room, then sends the LIVESTREAM_PROMPT with
   * the autogenerated `live-video/` URL to the Citizen
   */
  const handleInitVideoSession = async () => {
    // Track when user clicks to start VT911 session
    trackStartVideoTo911ButtonClick({
      fromOverviewTab: isForOverview,
      isUniteEventPaneEnabled: true,
    });

    // An existing video session has ended  if the card is open and
    // selectedVideoRoom is undefined
    if (isVideoCardOpen && !selectedVideoRoom) {
      dispatch(closeCard(LIVE_VIDEO_CARD));
    }

    // onStartConversation reuses an existing chat: when a TF911 chat is already
    // open for this caller it returns that conversation without re-hitting the
    // create endpoint (which would 409 and break room creation below).
    onStartConversation(preconfiguredMessages[0]).then(response => {
      const latestConversationId =
        response?.data?.conversation_id || getConversationID(conversation);

      if (response.isSuccess) {
        triggerCreateRoom({
          accountId,
          callerId: selectedCallerId,
          callStartTime,
        })
          .then(async response => {
            // Track the server's response to starting the VT911 session
            trackStartVT911Response({
              responseSuccess: !response?.error,
              roomId: response?.data?.room_id,
            });

            // Only send a message to Citizen if there isn't an active video
            // session
            if (
              response?.data &&
              response?.data?.room_id !== selectedVideoRoom?.room_id
            ) {
              const talkjsSession = window.__RSOS.talkjsSession;
              const currentChat =
                await talkjsSession.getOrCreateConversation(
                  latestConversationId,
                );

              const roomId = response.data.room_id;
              const refreshToken = response.data.refresh_token;

              const roomUrl = `${window.location.protocol}//${window.location.host}/live-video/rooms/${roomId}?refresh_token=${refreshToken}`;

              currentChat.sendMessage(`${LIVESTREAM_PROMPT} ${roomUrl}`);
            }
          })
          .then(() => {
            dispatch(setCardsToRender(LIVE_VIDEO_CARD));
          });
      }
    });
  };

  return {
    handleInitVideoSession,
  };
}
